import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Descriptions, Flex, Input, Modal, Typography } from 'antd'
import type { DescriptionsProps } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import {
  getConversation,
  setConversationOverride,
  sendAgentMessage,
} from '@/api/ai-chat/conversations'
import type {
  ConversationDetail,
  ConversationMessage,
} from '@/api/ai-chat/types'
import { fmtDevice, fmtMsgTime, fmtTime } from './utils'

const { Text, Link } = Typography

const SF_BASE = 'https://debtrelief.lightning.force.com/lightning/r/Lead'

interface Props {
  conversationId: string | null
  onClose: () => void
}

const OUTCOME_ENDED_LABEL: Record<string, string> = {
  appointment: 'Appointment booked',
  retention: 'Retention booked',
  dnq: 'DNQ',
  dnc: 'DNC',
}

export const ConversationModal: React.FC<Props> = ({
  conversationId,
  onClose,
}) => {
  const [conv, setConv] = useState<ConversationDetail | null>(null)
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [agentMsg, setAgentMsg] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const render = useCallback(async (id: string) => {
    const data = await getConversation(id)
    setConv(data.conversation)
    setMessages(data.messages)
  }, [])

  useEffect(() => {
    if (!conversationId) return
    render(conversationId)
    pollRef.current = setInterval(() => render(conversationId), 3000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [conversationId, render])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleToggleOverride = async () => {
    if (!conversationId) return
    const res = await setConversationOverride(conversationId)
    setConv((prev) =>
      prev ? { ...prev, agent_override: res.agent_override } : prev,
    )
  }

  const handleSendMessage = async () => {
    if (!conversationId || !agentMsg.trim()) return
    setSending(true)
    try {
      await sendAgentMessage(conversationId, agentMsg.trim())
      setAgentMsg('')
    } finally {
      setSending(false)
    }
  }

  const isInProgress = conv ? !conv.outcome : false
  const overrideActive = conv?.agent_override ?? false

  const leftLabel = (() => {
    if (
      !conv ||
      conv.outcome !== 'left' ||
      !conv.ended_at ||
      messages.length === 0
    )
      return 'User left'
    const lastMsgTime = new Date(messages[messages.length - 1].sent_at)
    const endedAt = new Date(conv.ended_at)
    return endedAt.getTime() - lastMsgTime.getTime() >= 60_000
      ? 'Closed by inactivity'
      : 'User left'
  })()

  const metaItems: DescriptionsProps['items'] = conv
    ? [
        {
          key: 'sf',
          label: 'SF ID',
          children: conv.prospect_id ? (
            <Link
              href={`${SF_BASE}/${conv.prospect_id}/view`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {conv.prospect_id}
            </Link>
          ) : (
            '—'
          ),
        },
        { key: 'stage', label: 'Stage', children: conv.current_stage },
        {
          key: 'outcome',
          label: 'Outcome',
          children: conv.outcome ?? 'in-progress',
        },
        ...(conv.debt_amount
          ? [
              {
                key: 'debt',
                label: 'Debt',
                children: '$' + Number(conv.debt_amount).toLocaleString(),
              },
            ]
          : []),
        {
          key: 'true_debt',
          label: 'True Debt',
          children: conv.true_debt_amount
            ? '$' + Number(conv.true_debt_amount).toLocaleString()
            : 'None',
        },
        ...(conv.appointment_id
          ? [{ key: 'appt', label: 'Appt ID', children: conv.appointment_id }]
          : []),
        ...(conv.timezone
          ? [{ key: 'tz', label: 'TZ', children: conv.timezone }]
          : []),
        ...(conv.device
          ? [
              {
                key: 'device',
                label: 'Device',
                children: fmtDevice(conv.device),
              },
            ]
          : []),
        {
          key: 'prompt',
          label: 'Prompt',
          children: conv.prompt_version ?? '—',
        },
        ...(conv.aff_id
          ? [{ key: 'aff', label: 'Aff ID', children: conv.aff_id }]
          : []),
      ]
    : []

  return (
    <Modal
      open={!!conversationId}
      onCancel={onClose}
      footer={null}
      width={860}
      style={{ maxWidth: '95vw' }}
      title={
        <Flex
          align="center"
          justify="space-between"
          style={{ paddingRight: 32 }}
        >
          <span>Conversation — {conv ? fmtTime(conv.started_at) : ''}</span>
          {isInProgress && (
            <Button
              size="small"
              type="primary"
              danger={overrideActive}
              style={
                overrideActive
                  ? undefined
                  : { background: '#f97316', borderColor: '#f97316' }
              }
              onClick={handleToggleOverride}
            >
              {overrideActive ? 'Resume AI' : 'Manual Override'}
            </Button>
          )}
        </Flex>
      }
    >
      {conv && (
        <>
          <Descriptions
            items={metaItems}
            size="small"
            style={{ marginBottom: 12 }}
            column={{ xs: 2, sm: 3, md: 4 }}
          />

          {/* Message thread */}
          <div
            style={{
              maxHeight: 400,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: '4px 0',
            }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  maxWidth: '75%',
                  padding: '9px 14px',
                  borderRadius:
                    m.role === 'assistant'
                      ? '4px 10px 10px 10px'
                      : '10px 4px 10px 10px',
                  alignSelf: m.role === 'assistant' ? 'flex-start' : 'flex-end',
                  background: m.role === 'assistant' ? '#f4f5f7' : '#2d9e5f',
                  border: m.role === 'assistant' ? '1px solid #e2e5ea' : 'none',
                  color: m.role === 'assistant' ? '#1a1a2e' : '#fff',
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                {m.content}
                <div
                  style={{
                    fontSize: 10,
                    opacity: 0.5,
                    marginTop: 4,
                    textAlign: m.role === 'assistant' ? 'left' : 'right',
                  }}
                >
                  {fmtMsgTime(m.sent_at)}
                </div>
              </div>
            ))}

            {/* Ended marker */}
            {conv.ended_at && (
              <Flex
                align="center"
                gap={8}
                style={{
                  margin: '12px 0',
                  fontSize: 11,
                  opacity: 0.45,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: 'currentColor',
                    opacity: 0.3,
                  }}
                />
                <span>
                  {conv.outcome === 'left'
                    ? leftLabel
                    : (OUTCOME_ENDED_LABEL[conv.outcome ?? ''] ?? 'Ended')}{' '}
                  — {fmtMsgTime(conv.ended_at)}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: 'currentColor',
                    opacity: 0.3,
                  }}
                />
              </Flex>
            )}
            {conv.appointment_time && (
              <Flex
                align="center"
                gap={8}
                style={{
                  marginTop: -8,
                  marginBottom: 12,
                  fontSize: 11,
                  opacity: 0.45,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: 'currentColor',
                    opacity: 0.3,
                  }}
                />
                <span>
                  Appointment scheduled for {fmtMsgTime(conv.appointment_time)}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: 'currentColor',
                    opacity: 0.3,
                  }}
                />
              </Flex>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Override input bar */}
          {isInProgress && overrideActive && (
            <div style={{ borderTop: '1px solid #e2e5ea', paddingTop: 10 }}>
              <Text
                type="secondary"
                style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
              >
                AI paused — you are controlling this conversation
              </Text>
              <Flex
                style={{
                  border: '1px solid #002244',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <Input.TextArea
                  value={agentMsg}
                  onChange={(e) => setAgentMsg(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                  placeholder="Type a message as agent…"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  style={{
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    resize: 'none',
                    fontSize: 14,
                  }}
                />
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  loading={sending}
                  onClick={handleSendMessage}
                  style={{ alignSelf: 'flex-end', margin: 4 }}
                />
              </Flex>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}
